import { Button, DetailBox } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Col, Container, Row } from 'reactstrap';
import GoBackToListButton from '../../../../components/buttons/GoBackToListButton';
import { PathsLayouts } from '../../../../config/routes';
import { TranslationsKeys } from '../../../../locales/translations';
import { formatDate } from '../../../../utils/dates';
import { useStudentsRiskHistoryDetails } from './loaders/StudentsRiskHistoryDetailsLoader';
import {
  StudentsRiskHistoryDetails,
  StudentsRiskHistoryDetailsOrigin,
} from './types';

type StudentsRiskHistoryDetailsHeaderProps = {
  origin?: StudentsRiskHistoryDetailsOrigin;
};

const StudentsRiskHistoryDetailsHeader = ({
  origin = 'riskSession',
}: StudentsRiskHistoryDetailsHeaderProps) => {
  const { t } = useTranslation(TranslationsKeys.STUDENT_RISK_HISTORY);
  const { result } = useStudentsRiskHistoryDetails();
  const history = useHistory();

  const studentId = useMemo(() => {
    if (!result?.success || !result?.data) {
      return null;
    }
    const data = result.data as StudentsRiskHistoryDetails;

    if (data.history?.length) {
      return data.history[0].studyPlanEnrollment.studentId;
    }
    if (data.actions?.length) {
      return data.actions[0].studentId;
    }
    return null;
  }, [result]);

  const columns = useMemo(() => {
    if (!result?.success || !result?.data) {
      return [];
    }
    const items = [
      {
        title: t('historyBox.year'),
        body: result?.data?.data?.boxData?.year?.toString() || '',
      },
      {
        title: t('historyBox.period'),
        body: result?.data?.data?.boxData?.period || '',
      },
      {
        title: t('historyBox.school'),
        body: result?.data?.data?.boxData?.school || '',
      },
    ];

    if (result?.data?.data?.boxData?.dates?.length) {
      items.unshift({
        title: t('historyBox.dates'),
        body:
          result?.data?.data?.boxData?.dates
            ?.map((date) => formatDate(date, 'DD/MM/YYYY'))
            .join(' - ') || '',
      });
    }

    if (result?.data?.data?.boxData?.session) {
      items.unshift({
        title: t('historyBox.title'),
        body: result?.data?.data?.boxData?.session || '',
      });
    }

    return items;
  }, [result, t]);

  if (!result?.success || !result?.data) {
    return <></>;
  }

  return (
    <Container fluid>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} className="pt-3" md={6}>
          <GoBackToListButton />
        </Col>
        <Col xs={12} md={6}>
          <div>
            <DetailBox columns={columns} />
          </div>
        </Col>
        {origin !== 'studentFile' && (
          <Col xs={12}>
            <div
              className="d-flex flex-row align-items-center justify-content-end mt-3"
              style={{ gap: 12 }}
            >
              <Button
                text={t('historyBox.openStudentFile')}
                size="md"
                icon="eye"
                outlined
                disabled={!studentId}
                onClick={() => {
                  if (studentId) {
                    history.push(
                      `${PathsLayouts.studentFile}/update/${studentId}`,
                    );
                  }
                }}
              />
              <Button
                text={t('historyBox.downloadSummary')}
                size="md"
                icon="download"
                outlined
              />
            </div>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default StudentsRiskHistoryDetailsHeader;
